<template>
  <div
    class="avails-result"
    :data-status="availsProgress?.statusCode">
    <AvailsMenu
      v-if="availsProgress != null"
      :entity="entity"
      :disabled="availsProgress?.isRunning || availsProgress?.isError"
      @ui-mode-changed="uiMode = $event"
      @use-detailed-forecast="useDetailedForecast = $event"
      @forecast-mode-changed="forecastMode = $event"
      @download-avails-breakdown="downloadAvailsBreakdown"
      @view-contending-line-items="() => emits('viewContendingLineItems')"
      @export-graph="(format) => availsResultV2.downloadGraph(format)" />

    <!-- Report status -->
    <report-progress-status
      v-if="availsProgress != null && (availsProgress.isRunning || availsProgress.isError)"
      :report-progress="availsProgress"
      :in-progress-message="$t('messages.checkingAvails')" />

    <template v-if="availsProgress?.isSuccess">
      <!-- Report summary details - Legacy UI -->
      <AvailsResultLegacy
        v-if="uiMode === UIMode.Legacy"
        :show-border="showBorder"
        :is-checking-avails="isCheckingAvails"
        :check-avails-total-impressions-available="checkAvailsTotalImpressionsAvailable"
        :check-avails-result="checkAvailsResult"
        :is-strict-avails="isStrictAvails"
        :is-loading-sites="isLoadingSites"
        :did-fail-to-process-sites="didFailToProcessSites"
        :predictive-avails-confidence-level="predictiveAvailsConfidenceLevel"
        :should-show-avails-breakdown="shouldShowAvailsBreakdown"
        :check-avails-total-overbooked-reserved-impressions="checkAvailsTotalOverbookedReservedImpressions"
        :check-avails-audience-impressions-available="checkAvailsAudienceImpressionsAvailable"
        :check-avails-total-reserved-impressions-available="checkAvailsTotalReservedImpressionsAvailable"
        :check-avails-total-reserved-impressions-available-percentage="
          checkAvailsTotalReservedImpressionsAvailablePercentage
        "
        :check-avails-audience-reserved-impressions-available="checkAvailsAudienceReservedImpressionsAvailable"
        :check-avails-audience-reserved-impressions-available-percentage="
          checkAvailsAudienceReservedImpressionsAvailablePercentage
        "
        :check-avails-total-unreserved-impressions-available="checkAvailsTotalUnreservedImpressionsAvailable"
        :check-avails-total-unreserved-impressions-available-percentage="
          checkAvailsTotalUnreservedImpressionsAvailablePercentage
        "
        :check-avails-audience-overbooked-reserved-impressions="checkAvailsAudienceOverbookedReservedImpressions"
        :check-avails-audience-unreserved-impressions-available="checkAvailsAudienceUnreservedImpressionsAvailable"
        :check-avails-audience-unreserved-impressions-available-percentage="
          checkAvailsAudienceUnreservedImpressionsAvailablePercentage
        " />

      <!-- New UI -->
      <AvailsResultV2
        v-if="uiMode === UIMode.New"
        ref="availsResultV2"
        :entity="entity"
        :use-detailed-forecast="useDetailedForecast"
        :start-date="startDate"
        :end-date="endDate"
        :avails-result="availsProgress?.result"
        :purchase-type="purchaseType"
        :target-impressions="targetImpressions"
        :forecast-mode="forecastMode" />

      <div class="flex-h justify-content-end mt-3">
        <span class="generation-duration badge badge-xs badge-disabled-hollow">
          {{ i18n.global.t('messages.reportGeneratedInX', [availsProgress.durationInSecondsFormatted]) }}
        </span>
      </div>
    </template>

    <modal
      :visible="isDownloadModalVisible"
      :title="capitalize($t('actions.downloadTargetedScreens'))"
      :body="capitalize($t('messages.chooseAFileFormat'))"
      :button1-visible="true"
      :button1-caption="capitalize($t('actions.cancel'))"
      :button2-visible="false"
      @confirmed="isDownloadModalVisible = false"
      @cancelled="isDownloadModalVisible = false">
      <template #modal-after-form>
        <div class="flex-v-full">
          <div class="file-types">
            <div class="file-type">
              <a
                href="#"
                data-action-type="download-csv-file"
                @click="() => downloadAvailsBreakdown('screens', 'CSV')">
                <icon-file-c-s-v />
              </a>
            </div>

            <div class="file-type">
              <a
                href="#"
                data-action-type="download-xslx-file"
                @click="() => downloadAvailsBreakdown('screens', 'EXCEL')">
                <icon-file-x-l-s />
              </a>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
  import axios, { CancelTokenSource } from 'axios';
  import Excel from 'exceljs';
  import { DateTime } from 'luxon';
  import { iif, interval, mergeMap, of, skipWhile, Subscription, take, tap, throwError, withLatestFrom } from 'rxjs';
  import { capitalize, computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue';

  import ReportProgressStatus from '@/common/components/ReportProgressStatus.vue';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
  import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
  import { IconFileCSV, IconFileXLS } from '@/common/icons/monochrome';
  import { LineItem, Package, Plan } from '@/common/models';
  import AvailsProgress from '@/common/models/avails/AvailsProgress';
  import AvailsResult from '@/common/models/avails/AvailsResult';
  import { IImpressionsForUnit } from '@/common/models/avails/TotalImpressionsByUnit';
  import Geofence from '@/common/models/geo/Geofence';
  import { NetworkFrequencyCap } from '@/common/models/targeting-criteria/NetworkFrequencyCap';
  import { roundToSixDecimals, toServerDate } from '@/globals';
  import DownloadUtils from '@/utils/DownloadUtils';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapExchAppActions, mapGetters, mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  import AvailsMenu from './avails/AvailsMenu.vue';
  import AvailsResultEnum from './avails/AvailsResult.enum';
  import AvailsResultLegacy from './avails/AvailsResultLegacy.vue';
  import AvailsResultV2 from './avails/AvailsResultV2.vue';
  import ForecastMode from './avails/ForcastMode.enum';
  import UIMode from './avails/UIMode.enum';
  import Modal from './controls/Modal.vue';

  const STATUS_ERROR = 'ERROR';

  interface IProps {
    adDuration: number;
    advertiserID?: number;
    budget: number;
    budgetMode: string;
    cadence?: number;
    campaignID?: number;
    campaignMinSecondsBetweenPlays?: number;
    cpm?: number;
    daypart: string;
    demographicIDs: number[];
    endDate: DateTime;
    entity: LineItem | Plan | Package;
    geofenceGroupIDs: number[];
    geofenceGroupExclusionIDs: number[];
    geofences?: Geofence[];
    impressions?: number;
    lineItemID: number;
    languageExclusionIDs: number[];
    languageIDs: number[];
    locationExclusionIDs: number[];
    locationIDs: number[];
    mediaTypeIDs: number[];
    mediaTypeExclusionIDs: number[];
    marketIDs: number[];
    marketExclusionIDs: number[];
    minSecondsBetweenPlays: number;
    networkIDs: number[];
    networkExclusionIDs: number[];
    networkFrequencyCaps: NetworkFrequencyCap[];
    priority?: number;
    purchaseType: PurchaseTypesEnum;
    showBorder?: boolean;
    siteExclusionIDs: number[];
    siteIDs: number[];
    startDate: DateTime;
    unitExclusionIDs: number[];
    unitIDs: number[];
    unitPackIDs: number[];
    unitPackExclusionIDs: number[];
    organizationID: number;
    bom?: string;
    creativeFormats: string[];
    creativeFormatExclusions: string[];
    creativeOrientations: CreativeOrientationType[];
    creativeOrientationExclusions: CreativeOrientationType[];
    iabCategories: string[];
    iabCategoryExclusions: string[];
    unitTags: string[];
    unitTagExclusions: string[];
    targetImpressions?: number;
  }

  const props = withDefaults(defineProps<IProps>(), {
    advertiserID: null,
    bom: '\uFEFF',
    cadence: null,
    campaignID: null,
    campaignMinSecondsBetweenPlays: null,
    cpm: null,
    geofences: null,
    impressions: null,
    priority: null,
    showBorder: false,
    targetImpressions: null,
  });

  interface IEmits {
    (e: 'availsResultChanged', value: typeof availsResult.value): void;
    (e: 'checkAvailsStarted'): void;
    (e: 'checkAvailsEnded'): void;
    (e: 'viewContendingLineItems'): void;
  }

  const emits = defineEmits<IEmits>();

  const availsProgress: Ref<AvailsProgress> = ref();
  const availsResultV2: Ref<InstanceType<typeof AvailsResultV2>> = ref();

  const columnsInSiteExport = [
    capitalize(i18n.global.t('names.siteID')),
    capitalize(i18n.global.t('names.siteName')),
    capitalize(i18n.global.t('names.siteLocationCity')),
    capitalize(i18n.global.t('names.siteRegion')),
    capitalize(i18n.global.t('names.siteLatitude')),
    capitalize(i18n.global.t('names.siteLongitude')),
    capitalize(i18n.global.t('names.screenNetwork')),
    capitalize(i18n.global.t('names.screenCount')),
    capitalize(i18n.global.t('messages.siteTotalImpressions')),
    capitalize(i18n.global.t('messages.siteAvailableTotalImpressions')),
    capitalize(i18n.global.t('messages.siteAvailableReservedImpressions')),
    capitalize(i18n.global.t('messages.siteAvailableUnreservedImpressions')),
  ];
  const columnsInUnitExport = [
    capitalize(i18n.global.t('names.screenID')),
    capitalize(i18n.global.t('names.screenName')),
    capitalize(i18n.global.t('messages.screenLocationCity')),
    capitalize(i18n.global.t('messages.screenRegion')),
    capitalize(i18n.global.t('names.screenLatitude')),
    capitalize(i18n.global.t('names.screenLongitude')),
    capitalize(i18n.global.t('names.screenWidthPx')),
    capitalize(i18n.global.t('names.screenHeightPx')),
    capitalize(i18n.global.t('names.screenNetwork')),
    capitalize(i18n.global.t('names.siteID')),
    capitalize(i18n.global.t('names.siteName')),
    capitalize(i18n.global.t('messages.screenMaxImpressionsCapacity')),
    capitalize(i18n.global.t('messages.screenAvailableTotalImpressions')),
    capitalize(i18n.global.t('messages.screenAvailableReservedImpressions')),
    capitalize(i18n.global.t('messages.screenAvailableUnreservedImpressions')),
    capitalize(i18n.global.t('messages.screenMaxCadence')),
    capitalize(i18n.global.t('messages.screenMaxHistoricalCadence')),
  ];
  const bookedImpressionsColumnsInUnitExport = {
    GUARANTEED_DEALS: capitalize(i18n.global.t('messages.screenBookedImpressionsGuaranteedDeals')),
    GUARANTEED_LINEITEMS: capitalize(i18n.global.t('messages.screenBookedImpressionsGuaranteedLineItems')),
    UNGUARANTEED: capitalize(i18n.global.t('messages.screenBookedImpressionsUnguaranteed')),
    HOUSE_AD: capitalize(i18n.global.t('messages.screenBookedImpressionsHouseAd')),
    CADENCE_BUY: capitalize(i18n.global.t('messages.screenBookedImpressionsCadenceBuy')),
    BONUS: capitalize(i18n.global.t('messages.screenBookedImpressionsBonus')),
  };
  const bookedImpressionsColumnsInSiteExport = {
    GUARANTEED_DEALS: capitalize(i18n.global.t('messages.siteBookedImpressionsGuaranteedDeals')),
    GUARANTEED_LINEITEMS: capitalize(i18n.global.t('messages.siteBookedImpressionsGuaranteedLineItems')),
    UNGUARANTEED: capitalize(i18n.global.t('messages.siteBookedImpressionsUnguaranteed')),
    HOUSE_AD: capitalize(i18n.global.t('messages.siteBookedImpressionsHouseAd')),
    CADENCE_BUY: capitalize(i18n.global.t('messages.siteBookedImpressionsCadenceBuy')),
    BONUS: capitalize(i18n.global.t('messages.siteBookedImpressionsBonus')),
  };

  const OVERBOOKED_RES_IMPS_SITE_COLUMN_IDX = 11;
  const AVAILABLE_IMPS_TOTAL_UNIT_COLUMN_IDX = 13;
  const OVERBOOKED_RES_IMPS_UNIT_COLUMN_IDX = 14;

  const cancelGetAvailsResultTokenSource: Ref<CancelTokenSource> = ref();
  const checkAvailsResultBySite = ref();
  const checkAvailsResultByUnit = ref();
  const checkAvailsTotalImpressionsAvailable: Ref<string> = ref();
  const checkAvailsAudienceImpressionsAvailable: Ref<string> = ref();
  const checkAvailsResult: Ref<AvailsResultEnum> = ref();
  const checkAvailsTotalReservedImpressionsAvailable: Ref<string> = ref();
  const checkAvailsTotalOverbookedReservedImpressions: Ref<string> = ref();
  const checkAvailsTotalUnreservedImpressionsAvailable: Ref<string> = ref();
  const checkAvailsTotalUnreservedImpressionsAvailablePercentage: Ref<string> = ref();
  const checkAvailsTotalReservedImpressionsAvailablePercentage: Ref<string> = ref();
  const checkAvailsAudienceReservedImpressionsAvailable: Ref<string> = ref();
  const checkAvailsAudienceOverbookedReservedImpressions: Ref<string> = ref();
  const checkAvailsAudienceUnreservedImpressionsAvailable: Ref<string> = ref();
  const checkAvailsAudienceUnreservedImpressionsAvailablePercentage: Ref<string> = ref();
  const checkAvailsAudienceReservedImpressionsAvailablePercentage: Ref<string> = ref();
  const closestCadenceImpressionsAvailable: Ref<number> = ref();
  const hasThrownError: Ref<boolean> = ref(false);
  const isStrictAvails: Ref<boolean> = ref(false);
  const didFailToProcessSites: Ref<boolean> = ref(false);
  const isCheckingAvails: Ref<boolean> = ref(false);
  const isDownloadModalVisible: Ref<boolean> = ref(false);
  const isLoadingSites: Ref<boolean> = ref(false);
  const cadenceRange = ref();
  const cadenceImpressionsRange = ref();
  const maxCadenceAvailable = ref();
  const maxCadenceImpressionsAvailable = ref();
  const floorCadenceFeasibility = ref();
  const optimizedCadenceFeasibility = ref();
  const pollingObserver: Ref<Subscription> = ref();
  const predictiveAvailsConfidenceLevel = ref();
  const ceilCadenceFeasibility = ref();
  const maxCadenceFeasibility = ref();
  const closestCadenceAvailable = ref();
  const optimizedImpressions = ref();
  const unitsBookedImpressionsColumns = ref([]);
  const sitesBookedImpressionsColumns = ref([]);
  const name: Ref<string> = ref();

  const { organizationSettings } = mapOrgAppState();
  const uiMode: Ref<UIMode> = ref(UIMode.New);
  const useDetailedForecast = ref(false);
  const forecastMode: Ref<ForecastMode> = ref(ForecastMode.Inventory);

  onMounted(() => {
    if (organizationSettings?.enableOverbookedReservedImpressions) {
      columnsInSiteExport.splice(
        OVERBOOKED_RES_IMPS_SITE_COLUMN_IDX,
        0,
        capitalize(i18n.global.t('messages.siteOverbookedReservedImpressions')),
      );
      columnsInUnitExport.splice(
        OVERBOOKED_RES_IMPS_UNIT_COLUMN_IDX,
        0,
        capitalize(i18n.global.t('messages.screenOverbookedReservedImpressions')),
      );
    }
  });

  onUnmounted(() => {
    // unsubscribe from report generation polling before unmount
    pollingObserver.value?.unsubscribe();
  });

  const shouldShowAvailsBreakdown = computed(() => {
    return organizationSettings.externalAvailsUrl !== 'full-adserver-availability';
  });

  const availsResult = computed(() => {
    return {
      checkAvailsResultBySite: checkAvailsResultBySite.value,
      checkAvailsResultByUnit: checkAvailsResultByUnit.value,
      checkAvailsTotalImpressionsAvailable: checkAvailsTotalImpressionsAvailable.value,
      checkAvailsAudienceImpressionsAvailable: checkAvailsAudienceImpressionsAvailable.value,
      checkAvailsResult: checkAvailsResult.value,
      checkAvailsTotalReservedImpressionsAvailable: checkAvailsTotalReservedImpressionsAvailable.value,
      checkAvailsTotalOverbookedReservedImpressions: checkAvailsTotalOverbookedReservedImpressions.value,
      checkAvailsTotalUnreservedImpressionsAvailable: checkAvailsTotalUnreservedImpressionsAvailable.value,
      checkAvailsTotalUnreservedImpressionsAvailablePercentage:
        checkAvailsTotalUnreservedImpressionsAvailablePercentage.value,
      checkAvailsTotalReservedImpressionsAvailablePercentage:
        checkAvailsTotalReservedImpressionsAvailablePercentage.value,
      checkAvailsAudienceReservedImpressionsAvailable: checkAvailsAudienceReservedImpressionsAvailable.value,
      checkAvailsAudienceOverbookedReservedImpressions: checkAvailsAudienceOverbookedReservedImpressions.value,
      checkAvailsAudienceUnreservedImpressionsAvailable: checkAvailsAudienceUnreservedImpressionsAvailable.value,
      checkAvailsAudienceUnreservedImpressionsAvailablePercentage:
        checkAvailsAudienceUnreservedImpressionsAvailablePercentage.value,
      checkAvailsAudienceReservedImpressionsAvailablePercentage:
        checkAvailsAudienceReservedImpressionsAvailablePercentage.value,
      isStrictAvails: isStrictAvails.value,
      didFailToProcessSites: didFailToProcessSites.value,
      cadenceRange: cadenceRange.value,
      cadenceImpressionsRange: cadenceImpressionsRange.value,
      maxCadenceAvailable: maxCadenceAvailable.value,
      maxCadenceImpressionsAvailable: maxCadenceImpressionsAvailable.value,
      floorCadenceFeasibility: floorCadenceFeasibility.value,
      optimizedCadenceFeasibility: optimizedCadenceFeasibility.value,
      predictiveAvailsConfidenceLevel: predictiveAvailsConfidenceLevel.value,
      ceilCadenceFeasibility: ceilCadenceFeasibility.value,
      maxCadenceFeasibility: maxCadenceFeasibility.value,
      closestCadenceAvailable: closestCadenceAvailable.value,
      optimizedImpressions: optimizedImpressions.value,
    };
  });

  const { hasChosenOrganization, hasChosenExchange } = mapGetters();

  const {
    getAvailsByUnitPost: orgGetAvails,
    getAvailsExecutionById: orgGetAvailsProgress,
    getUnitsBrief: getUnitsBrief,
    getSitesBriefUsingPost: getSitesBriefUsingPost,
  } = mapOrgAppActions();

  const { getAvailsByUnitPost: exchGetAvails, getAvailsExecutionById: exchGetAvailsProgress } = mapExchAppActions();

  function getColumnNameForBookedImpressions(purchaseType) {
    return {
      unitColumnName:
        purchaseType in bookedImpressionsColumnsInUnitExport
          ? bookedImpressionsColumnsInUnitExport[purchaseType]
          : purchaseType,
      siteColumnName:
        purchaseType in bookedImpressionsColumnsInSiteExport
          ? bookedImpressionsColumnsInSiteExport[purchaseType]
          : purchaseType,
    };
  }

  async function checkAvails(getDetailedAvails = true, checkForOptimizedImpressions = false) {
    isCheckingAvails.value = true;
    hasThrownError.value = false;
    const params = {
      start_date: toServerDate(props.startDate),
      end_date: toServerDate(props.endDate),
      ad_duration: props.adDuration != null ? props.adDuration : 0,
      organization_id: props.organizationID,
      get_detailed: getDetailedAvails,
      targeting_criteria: {
        advertiser_id: props.advertiserID !== null ? props.advertiserID : undefined,
        budget: props.budgetMode === BudgetModes.BUDGET ? props.budget : null,
        budget_mode: props.budgetMode,
        cadence: props.cadence,
        campaign_id: props.campaignID !== null ? props.campaignID : undefined,
        campaign_min_seconds_between_plays:
          props.campaignMinSecondsBetweenPlays !== null ? props.campaignMinSecondsBetweenPlays : undefined,
        cpm: props.cpm,
        creative_format_exclusions: props.creativeFormatExclusions,
        creative_formats: props.creativeFormats,
        creative_orientation_exclusions: props.creativeOrientationExclusions,
        creative_orientations: props.creativeOrientations,
        iab_categories: props.iabCategories,
        iab_category_exclusions: props.iabCategoryExclusions,
        daypart: props.daypart,
        demographic_ids: props.demographicIDs,
        geofencegroup_exclusion_ids: props.geofenceGroupExclusionIDs,
        geofencegroup_ids: props.geofenceGroupIDs,
        geofences: props.geofences?.map((g) => g.toApi().geojson) || [],
        impressions: props.budgetMode === BudgetModes.IMPRESSIONS ? props.impressions : null,
        language_exclusion_ids: props.languageExclusionIDs,
        language_ids: props.languageIDs,
        lineitem_id: props.lineItemID,
        lineitem_min_seconds_between_plays: props.minSecondsBetweenPlays,
        lineitem_networkfreqcaps:
          props.networkFrequencyCaps?.map((networkFrequencyCap) => networkFrequencyCap.toApi()) || [],
        location_exclusion_ids: props.locationExclusionIDs,
        location_ids: props.locationIDs,
        market_exclusion_ids: props.marketExclusionIDs,
        market_ids: props.marketIDs,
        mediatype_exclusion_ids: props.mediaTypeExclusionIDs,
        mediatype_ids: props.mediaTypeIDs,
        network_exclusion_ids: props.networkExclusionIDs,
        network_ids: props.networkIDs,
        priority: props.priority,
        purchase_type: props.purchaseType,
        site_exclusion_ids: props.siteExclusionIDs,
        site_ids: props.siteIDs,
        unit_exclusion_ids: props.unitExclusionIDs,
        unit_ids: props.unitIDs,
        unit_tag_exclusions: props.unitTagExclusions,
        unit_tags: props.unitTags,
        unitpack_exclusion_ids: props.unitPackExclusionIDs,
        unitpack_ids: props.unitPackIDs,
      },
      cancelTokenSource: cancelGetAvailsResultTokenSource.value,
      run_in_background: true,
      checkForOptimizedImpressions: null,
    };
    const processAvailsResult = async (result: AvailsResult) => {
      if (result == null) return; // Avails cancelled.

      isStrictAvails.value = result.strictAvails;
      if (result.audienceImpressionsResult === null || result.audienceImpressionsResult === undefined) {
        checkAvailsTotalImpressionsAvailable.value = null;
        checkAvailsTotalReservedImpressionsAvailable.value = null;
        checkAvailsTotalOverbookedReservedImpressions.value = null;
        checkAvailsTotalUnreservedImpressionsAvailable.value = null;
        checkAvailsTotalUnreservedImpressionsAvailablePercentage.value = null;
        checkAvailsTotalReservedImpressionsAvailablePercentage.value = null;
        checkAvailsAudienceReservedImpressionsAvailable.value = null;
        checkAvailsAudienceOverbookedReservedImpressions.value = null;
        checkAvailsAudienceUnreservedImpressionsAvailable.value = null;
        checkAvailsAudienceUnreservedImpressionsAvailablePercentage.value = null;
        checkAvailsAudienceReservedImpressionsAvailablePercentage.value = null;

        checkAvailsResult.value = null;
        showUserNotificationError();
      } else {
        predictiveAvailsConfidenceLevel.value = result.predictiveAvailsConfidenceLevel;
        const totalImpressionsMin =
          result.totalImpressionsResult.impressionsAvailable *
          (1 - result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        const totalImpressionsMax =
          result.totalImpressionsResult.impressionsAvailable *
          (1 + result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        checkAvailsTotalImpressionsAvailable.value = `${formatNumber(totalImpressionsMin, '0.0a')} - ${formatNumber(totalImpressionsMax, '0.0a')}`;

        const totalReservedImpressionsMin =
          result.totalImpressionsResult.reservedImpressions *
          (1 - result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        const totalReservedImpressionsMax =
          result.totalImpressionsResult.reservedImpressions *
          (1 + result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        checkAvailsTotalReservedImpressionsAvailablePercentage.value = formatNumber(
          roundToSixDecimals(
            result.totalImpressionsResult.reservedImpressions / result.totalImpressionsResult.impressionsAvailable,
          ),
          '0.0%',
        );
        checkAvailsTotalReservedImpressionsAvailable.value =
          result.totalImpressionsResult.reservedImpressions === 0
            ? '0'
            : `${formatNumber(totalReservedImpressionsMin, '0.0a')} - ${formatNumber(totalReservedImpressionsMax, '0.0a')}`;

        if (organizationSettings.enableOverbookedReservedImpressions) {
          if (
            result.totalImpressionsResult.overbookedReservedImpressions === null ||
            result.totalImpressionsResult.overbookedReservedImpressions === undefined
          ) {
            checkAvailsTotalOverbookedReservedImpressions.value = '0';
          } else {
            const totalOverbookedReservedImpressionsMin =
              result.totalImpressionsResult.overbookedReservedImpressions *
              (1 - result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
            const totalOverbookedReservedImpressionsMax =
              result.totalImpressionsResult.overbookedReservedImpressions *
              (1 + result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
            checkAvailsTotalOverbookedReservedImpressions.value =
              result.totalImpressionsResult.overbookedReservedImpressions === 0
                ? '0'
                : `${formatNumber(totalOverbookedReservedImpressionsMin, '0.0a')} - ${formatNumber(totalOverbookedReservedImpressionsMax, '0.0a')}`;
          }
        }

        const totalUnreservedImpressionsMin =
          result.totalImpressionsResult.unreservedImpressions *
          (1 - result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        const totalUnreservedImpressionsMax =
          result.totalImpressionsResult.unreservedImpressions *
          (1 + result.totalImpressionsResult.accuracyPercentageOff / 100 / 2);
        checkAvailsTotalUnreservedImpressionsAvailablePercentage.value = formatNumber(
          roundToSixDecimals(
            result.totalImpressionsResult.unreservedImpressions / result.totalImpressionsResult.impressionsAvailable,
          ),
          '0.0%',
        );
        checkAvailsTotalUnreservedImpressionsAvailable.value =
          result.totalImpressionsResult.unreservedImpressions === 0
            ? '0'
            : `${formatNumber(totalUnreservedImpressionsMin, '0.0a')} - ${formatNumber(totalUnreservedImpressionsMax, '0.0a')}`;

        //
        // Audience impressions
        //
        if (result.audienceImpressionsResult.demographicIds !== null) {
          checkAvailsAudienceImpressionsAvailable.value =
            result.audienceImpressionsResult.impressionsAvailableFormatted;

          const reservedPercentage = roundToSixDecimals(
            result.totalImpressionsResult.reservedImpressions / result.totalImpressionsResult.impressionsAvailable,
          );
          const unreservedPercentage = roundToSixDecimals(
            result.totalImpressionsResult.unreservedImpressions / result.totalImpressionsResult.impressionsAvailable,
          );

          const audienceReservedImpressionsMin = roundToSixDecimals(
            result.audienceImpressionsResult.impressionsAvailable *
              reservedPercentage *
              (1 - result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
          );
          const audienceReservedImpressionsMax = roundToSixDecimals(
            result.audienceImpressionsResult.impressionsAvailable *
              reservedPercentage *
              (1 + result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
          );
          checkAvailsAudienceReservedImpressionsAvailablePercentage.value = formatNumber(reservedPercentage, '0.0%');
          checkAvailsAudienceReservedImpressionsAvailable.value =
            result.audienceImpressionsResult.reservedImpressions === 0
              ? '0'
              : `${formatNumber(audienceReservedImpressionsMin, '0.0a')} - ${formatNumber(audienceReservedImpressionsMax, '0.0a')}`;

          const audienceUnreservedImpressionsMin = roundToSixDecimals(
            result.audienceImpressionsResult.impressionsAvailable *
              unreservedPercentage *
              (1 - result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
          );
          const audienceUnreservedImpressionsMax = roundToSixDecimals(
            result.audienceImpressionsResult.impressionsAvailable *
              unreservedPercentage *
              (1 + result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
          );
          checkAvailsAudienceUnreservedImpressionsAvailablePercentage.value = formatNumber(
            unreservedPercentage,
            '0.0%',
          );
          checkAvailsAudienceUnreservedImpressionsAvailable.value =
            result.audienceImpressionsResult.unreservedImpressions === 0
              ? '0'
              : `${formatNumber(audienceUnreservedImpressionsMin, '0.0a')} - ${formatNumber(audienceUnreservedImpressionsMax, '0.0a')}`;

          if (organizationSettings.enableOverbookedReservedImpressions) {
            if (
              result.totalImpressionsResult.overbookedReservedImpressions === null ||
              result.totalImpressionsResult.overbookedReservedImpressions === undefined
            ) {
              checkAvailsAudienceOverbookedReservedImpressions.value = '0';
            } else {
              const overbookedReservedPercentage = roundToSixDecimals(
                result.totalImpressionsResult.overbookedReservedImpressions /
                  result.totalImpressionsResult.impressionsAvailable,
              );
              const audienceOverbookedReservedImpsMin = roundToSixDecimals(
                result.audienceImpressionsResult.impressionsAvailable *
                  overbookedReservedPercentage *
                  (1 - result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
              );
              const audienceOverbookedReservedImpsMax = roundToSixDecimals(
                result.audienceImpressionsResult.impressionsAvailable *
                  overbookedReservedPercentage *
                  (1 + result.audienceImpressionsResult.accuracyPercentageOff / 100 / 2),
              );
              checkAvailsAudienceOverbookedReservedImpressions.value =
                result.totalImpressionsResult.overbookedReservedImpressions === 0
                  ? '0'
                  : `${formatNumber(audienceOverbookedReservedImpsMin, '0.0a')} - ${formatNumber(audienceOverbookedReservedImpsMax, '0.0a')}`;
            }
          }
        } else {
          checkAvailsAudienceImpressionsAvailable.value = checkAvailsTotalImpressionsAvailable.value;
          checkAvailsAudienceReservedImpressionsAvailablePercentage.value =
            checkAvailsTotalReservedImpressionsAvailablePercentage.value;
          checkAvailsAudienceOverbookedReservedImpressions.value = checkAvailsTotalOverbookedReservedImpressions.value;
          checkAvailsAudienceReservedImpressionsAvailable.value = checkAvailsTotalReservedImpressionsAvailable.value;
          checkAvailsAudienceUnreservedImpressionsAvailablePercentage.value =
            checkAvailsTotalUnreservedImpressionsAvailablePercentage.value;
          checkAvailsAudienceUnreservedImpressionsAvailable.value =
            checkAvailsTotalUnreservedImpressionsAvailable.value;
        }

        //
        // Cadence
        //
        if (result.totalImpressionsResult != null) {
          cadenceRange.value = result.totalImpressionsResult.cadenceRange;
          cadenceImpressionsRange.value = result.totalImpressionsResult.cadenceImpressionsRange;
          maxCadenceAvailable.value = result.totalImpressionsResult.maxCadenceAvailable;
          maxCadenceImpressionsAvailable.value = result.totalImpressionsResult.maxCadenceImpressionsAvailable;
          closestCadenceAvailable.value = result.totalImpressionsResult.closestCadenceAvailable;
          closestCadenceImpressionsAvailable.value = result.totalImpressionsResult.closestCadenceImpressionsAvailable;
          checkAvailsResult.value = result.availabilityResult as AvailsResultEnum;
        }
        if (result.cadenceImpressionFeasibility !== null) {
          floorCadenceFeasibility.value = result.cadenceImpressionFeasibility.floorCadence;
          optimizedCadenceFeasibility.value = result.cadenceImpressionFeasibility.optimizedCadence;
          ceilCadenceFeasibility.value = result.cadenceImpressionFeasibility.ceilCadence;
          maxCadenceFeasibility.value = result.cadenceImpressionFeasibility.maxCadence;
        }
        // Get columns with booked impressions by purchase type by unit/site
        unitsBookedImpressionsColumns.value = [];
        sitesBookedImpressionsColumns.value = [];
        if (result.totalImpressionsByUnit.bookedImpressionsByPurchaseTypeByUnit !== undefined) {
          const purchaseTypes = Object.keys(result.totalImpressionsByUnit.bookedImpressionsByPurchaseTypeByUnit);
          // we need to sort the columns to ensure consistency
          purchaseTypes.sort();
          purchaseTypes.forEach((purchaseType) => {
            const { unitColumnName, siteColumnName } = getColumnNameForBookedImpressions(purchaseType);
            unitsBookedImpressionsColumns.value.push(unitColumnName);
            sitesBookedImpressionsColumns.value.push(siteColumnName);
          });
        }

        optimizedImpressions.value = result.optimizedImpressions;
        isLoadingSites.value = true;
        //
        // Get units information and prepare data for download.
        //
        const processSites = async (siteToNetworkToUnitsMap) => {
          const uniqueSiteIDs = [...siteToNetworkToUnitsMap.keys()];
          const sitesBrief = await getSitesBriefUsingPost({
            siteIDs: uniqueSiteIDs,
            networkIDs: props.networkIDs,
            showLoader: false,
          });
          try {
            checkAvailsResultBySite.value = sitesBrief.map((siteBrief) => {
              const { siteID, dataRow, networkName } = siteBrief;
              let siteImpressionCapacity = 0;
              let siteTotalImpressions = 0;
              let siteReservedImpressions = 0;
              let siteOverbookedReservedImpressions = 0;
              let siteUnreservedImpressions = 0;
              const siteBookedImpressions = Array(sitesBookedImpressionsColumns.value.length).fill(0);

              if (siteToNetworkToUnitsMap.size && siteID && networkName) {
                const unitIDs = siteToNetworkToUnitsMap?.get(siteID)?.get(networkName);
                if (unitIDs != null) {
                  unitIDs.forEach((unitID) => {
                    const resultForUnit = result.impressionsForUnit(unitID) as IImpressionsForUnit;
                    siteImpressionCapacity += resultForUnit.impressionCapacity;
                    siteTotalImpressions += resultForUnit.totalImpressions;
                    siteReservedImpressions += resultForUnit.reservedImpressions;
                    siteUnreservedImpressions += resultForUnit.unreservedImpressions;
                    for (let i = 0; i < resultForUnit.bookedImpressions.length; i += 1) {
                      siteBookedImpressions[i] += resultForUnit.bookedImpressions[i];
                    }
                    if (resultForUnit.overbookedReservedImpressions !== undefined) {
                      siteOverbookedReservedImpressions += resultForUnit.overbookedReservedImpressions;
                    }
                  });
                }
              }
              if (organizationSettings.enableOverbookedReservedImpressions) {
                dataRow.push(
                  ...[
                    siteImpressionCapacity,
                    siteTotalImpressions,
                    siteReservedImpressions,
                    siteOverbookedReservedImpressions,
                    siteUnreservedImpressions,
                    ...siteBookedImpressions,
                  ],
                );
              } else {
                dataRow.push(
                  ...[
                    siteImpressionCapacity,
                    siteTotalImpressions,
                    siteReservedImpressions,
                    siteUnreservedImpressions,
                    ...siteBookedImpressions,
                  ],
                );
              }
              return dataRow;
            });
          } catch (e) {
            didFailToProcessSites.value = true;
          } finally {
            isLoadingSites.value = false;
          }
        };

        const siteToNetworkToUnitsMap = new Map();
        checkAvailsResultByUnit.value = [];
        const { unitIDs } = result;
        if (unitIDs != null) {
          let processedUnitsCount = 0;
          const processNextUnitBatch = async () => {
            const batchUnitIDs = unitIDs.slice(processedUnitsCount, processedUnitsCount + 1000);
            processedUnitsCount += 1000;
            const unitsBrief = await getUnitsBrief(batchUnitIDs);
            checkAvailsResultByUnit.value = [
              ...checkAvailsResultByUnit.value,
              ...unitsBrief.map((unit) => {
                const { unitID } = unit;
                if (!siteToNetworkToUnitsMap.has(unit.siteID)) {
                  siteToNetworkToUnitsMap.set(unit.siteID, new Map());
                }
                const networkToUnitsMap = siteToNetworkToUnitsMap.get(unit.siteID);
                if (!networkToUnitsMap.has(unit.networkName)) {
                  networkToUnitsMap.set(unit.networkName, []);
                }
                networkToUnitsMap.get(unit.networkName).push(unitID);
                const impressionsForUnit = result.impressionsForUnit(unitID) as IImpressionsForUnit;
                const {
                  impressionCapacity,
                  totalImpressions,
                  reservedImpressions,
                  overbookedReservedImpressions,
                  unreservedImpressions,
                  bookedImpressions,
                  maxCadence,
                  maxHistoricalCadence,
                } = impressionsForUnit;

                const dataRow = [
                  unit.externalID,
                  unit.name,
                  unit.city,
                  unit.region,
                  unit.latitude,
                  unit.longitude,
                  unit.screenWidth,
                  unit.screenHeight,
                  unit.networkName,
                  unit.siteExternalID,
                  unit.siteName,
                  impressionCapacity,
                  totalImpressions,
                  reservedImpressions,
                  unreservedImpressions,
                  maxCadence,
                  maxHistoricalCadence,
                  ...bookedImpressions,
                ];

                if (organizationSettings.enableOverbookedReservedImpressions) {
                  dataRow.splice(OVERBOOKED_RES_IMPS_UNIT_COLUMN_IDX, 0, overbookedReservedImpressions);
                }

                return dataRow;
              }),
            ];

            if (processedUnitsCount < unitIDs.length) {
              await processNextUnitBatch();
            } else {
              await processSites(siteToNetworkToUnitsMap);
            }
          };

          try {
            await processNextUnitBatch();
          } finally {
            isLoadingSites.value = false;
          }
        }
      }
      isCheckingAvails.value = false;
      emits('availsResultChanged', availsResult.value);
    };

    if (cancelGetAvailsResultTokenSource.value != null) {
      cancelGetAvailsResultTokenSource.value.cancel();
    }
    cancelGetAvailsResultTokenSource.value = axios.CancelToken.source();
    params.cancelTokenSource = cancelGetAvailsResultTokenSource.value;
    params.checkForOptimizedImpressions = checkForOptimizedImpressions;

    let getAvailsPromise: Promise<AvailsProgress> = Promise.resolve() as unknown as Promise<AvailsProgress>;
    let getAvailsProgress: (id: number) => Promise<AvailsProgress>;
    if (hasChosenOrganization.value) {
      getAvailsPromise = orgGetAvails(params);
      getAvailsProgress = orgGetAvailsProgress;
    } else if (hasChosenExchange.value) {
      getAvailsPromise = exchGetAvails({ params });
      getAvailsProgress = exchGetAvailsProgress;
    }

    pollingObserver.value = interval(2000)
      .pipe(
        withLatestFrom(getAvailsPromise),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        mergeMap(([_, avails]) => getAvailsProgress(avails.id)),
        mergeMap((progress: AvailsProgress) =>
          iif(
            () => {
              return (
                availsProgress.value == null ||
                availsProgress.value?.createdOnUtcDateTime?.toMillis() <= progress.createdOnUtcDateTime?.toMillis()
              );
            },
            of(progress),
            throwError(() => `${progress.id} canceled`),
          ),
        ),
        tap((progress) => {
          availsProgress.value = progress;
          // If the progress is an error state, stop the avails check
          if (availsProgress.value.statusCode === STATUS_ERROR) {
            isCheckingAvails.value = false;
            showUserNotificationError();
          }
        }),

        skipWhile((progress) => progress.isRunning && isCheckingAvails.value),
        take(1),
      )
      .subscribe(({ result }) => {
        try {
          processAvailsResult(result);
        } catch (e) {
          isCheckingAvails.value = false;
          if (!hasThrownError.value && availsProgress.value?.statusCode === STATUS_ERROR) {
            showUserNotificationError();
          }
        }
      });
  }

  function showUserNotificationError() {
    hasThrownError.value = true;
    UserNotifications.error({
      title: i18n.global.t('messages.unableToGetAvailabilities'),
      message: availsProgress.value?.statusMessage ?? '',
    });
  }

  function cancelCheckAvails() {
    isCheckingAvails.value = false;
  }

  function setUpSiteDownload(dataRows) {
    const workbook = new Excel.Workbook();

    workbook.views = [
      {
        x: 0,
        y: 0,
        width: 30000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    const sheet = workbook.addWorksheet('Sites');

    const allColumns = [...columnsInSiteExport, ...sitesBookedImpressionsColumns.value];
    sheet.addRow(allColumns);
    dataRows.forEach((row) => {
      sheet.addRow(row);
    });

    return workbook;
  }

  function setUpUnitDownload(dataRows) {
    const workbook = new Excel.Workbook();

    workbook.views = [
      {
        x: 0,
        y: 0,
        width: 30000,
        height: 20000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ];

    const sheet = workbook.addWorksheet(i18n.global.t('names.screen', { n: 2 }));
    const baseColumnWidth = 10;
    sheet.getColumn(1).width = 1 * baseColumnWidth; // Screen ID
    sheet.getColumn(2).width = 2.4 * baseColumnWidth; // Screen name
    sheet.getColumn(3).width = 1.4 * baseColumnWidth; // Screen location (city)
    sheet.getColumn(4).width = 1.4 * baseColumnWidth; // Screen region (state)
    sheet.getColumn(5).width = 1.4 * baseColumnWidth; // Screen latitude
    sheet.getColumn(6).width = 1.4 * baseColumnWidth; // Screen longitude
    sheet.getColumn(7).width = 2 * baseColumnWidth; // Screen width (px)
    sheet.getColumn(8).width = 1 * baseColumnWidth; // Screen height (px)
    sheet.getColumn(9).width = 2.4 * baseColumnWidth; // Screen network
    sheet.getColumn(10).width = 2 * baseColumnWidth; // Site ID
    sheet.getColumn(11).width = 2 * baseColumnWidth; // Site name
    // Avails info
    sheet.getColumn(12).width = 2 * baseColumnWidth; // Screen max impressions capacity
    sheet.getColumn(13).width = 3 * baseColumnWidth; // Screen available impressions - total
    sheet.getColumn(14).width = 3 * baseColumnWidth; // Screen available impressions - reserved
    sheet.getColumn(15).width = 3 * baseColumnWidth; // Screen available impressions - unreserved
    sheet.getColumn(16).width = 2 * baseColumnWidth; // Screen max cadence
    sheet.getColumn(17).width = 2 * baseColumnWidth; // Screen max historical cadence

    // Booked impressions columns
    for (let i = 0; i < unitsBookedImpressionsColumns.value.length; i += 1) {
      sheet.getColumn(18 + i).width = 3 * baseColumnWidth;
    }

    // number of avails columns that will be removed from the resulting workbook if shouldShowAvailsBreakdown is disabled (i.e: reserved/unreserved impressions columns)
    let availsColumns = 2;

    if (organizationSettings.enableOverbookedReservedImpressions) {
      availsColumns += 1; // In this case, we're adding a column for overbooked reserved imps
    }

    if (!shouldShowAvailsBreakdown.value) {
      // This removes the Screen available impressions - reserved and unreserved columns and the overbooked reserved imps if org setting is enabled
      columnsInUnitExport.splice(AVAILABLE_IMPS_TOTAL_UNIT_COLUMN_IDX, availsColumns);
      dataRows.forEach((row) => {
        row.splice(AVAILABLE_IMPS_TOTAL_UNIT_COLUMN_IDX, availsColumns);
      });
    }

    const displayedColumns = [...columnsInUnitExport, ...unitsBookedImpressionsColumns.value];

    sheet.addRow(displayedColumns);
    sheet.addRows(dataRows);

    return workbook;
  }

  async function saveCSV(workbook, nameSuffix) {
    workbook.csv.writeBuffer().then((data) => {
      const blob = new window.Blob([props.bom + data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const filename =
        name.value != null && name.value.trim() !== '' ? `${name.value} - ${nameSuffix}.csv` : `${nameSuffix}.csv`;
      DownloadUtils.saveBlobToFile(blob, filename);
      isDownloadModalVisible.value = false;
    });
  }

  async function saveXLSX(workbook, nameSuffix) {
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new window.Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const filename =
        name.value != null && name.value.trim() !== '' ? `${name.value} - ${nameSuffix}.xlsx` : `${nameSuffix}.xlsx`;
      DownloadUtils.saveBlobToFile(blob, filename);
      isDownloadModalVisible.value = false;
    });
  }

  async function downloadAvailsBreakdown(entity: 'screens' | 'site', format: 'CSV' | 'EXCEL') {
    if (entity == 'site') {
      const workbook = setUpSiteDownload(checkAvailsResultBySite.value);
      if (format === 'EXCEL') {
        await saveXLSX(workbook, 'site');
      }
      if (format === 'CSV') {
        await saveCSV(workbook, 'site');
      }
    }
    if (entity == 'screens') {
      const workbook = setUpUnitDownload(checkAvailsResultByUnit.value);
      if (format === 'EXCEL') {
        await saveXLSX(workbook, 'screens');
      }
      if (format === 'CSV') {
        await saveCSV(workbook, 'screens');
      }
    }
  }

  watch(isCheckingAvails, (val) => {
    if (val) {
      emits('checkAvailsStarted');
    } else {
      emits('checkAvailsEnded');
    }
  });

  defineExpose({
    checkAvails,
    checkAvailsResult,
    cancelGetAvailsResultTokenSource,
    cancelCheckAvails,
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .avails-result {
      font-size: 1.4rem;
      font-weight: 400;

      .generation-duration {
        display: flex;
        justify-content: end;
      }
    }
  }
</style>
